import { pick } from 'lodash/fp';
import { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import WorkflowEditor from 'dashboard/components/WorkflowEditor';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { TemplateFilter } from 'dashboard/lib/workflows/objects';
import { AccountAction } from 'dashboard/models/account-action';
import {
  Workflow,
  WorkflowConfigError,
  WorkflowErrorType,
  WorkflowSchema,
} from 'dashboard/models/workflow';
import { WorkflowBlock } from 'dashboard/models/workflow-block';
import { WorkflowDeployment } from 'dashboard/models/workflow-deployment';
import {
  WorkflowStepInput,
  WorkflowVersion,
  WorkflowVersionStatus,
} from 'dashboard/models/workflow-version';
import { ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import AccountActionHeader from 'dashboard/views/DashboardAccountActionsShowView/AccountActionHeader';
import WorkflowStatusBanner from 'dashboard/views/DashboardWorkflowsShowView/WorkflowStatusBanner';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

export interface DashboardAccountActionsShowViewProps {
  isLoading: boolean;
  isDirty: boolean;
  accountTypeId: string;
  accountAction: AccountAction;
  actionWorkflow: Workflow;
  actionWorkflowVersion: WorkflowVersion;
  actionWorkflowDeployment: WorkflowDeployment | null;
  validationErrors: WorkflowConfigError[];
  editingWorkflowVersionId: string;
  workflowSchema: WorkflowSchema;
  inputStep: WorkflowStepInput;
  isLoadingVersionList: boolean;
  versionsList: (WorkflowVersion | WorkflowBlock)[];
  handleSaveAccountAction: () => Promise<void>;
  handlePublishAccountAction: () => Promise<void>;
  handleArchiveAccountAction: () => Promise<void>;
  handleActivateAccountAction: () => Promise<void>;
  handleDeactivateAccountAction: () => Promise<void>;
  handleUpdateAccountActionName: (name: string) => Promise<void>;
  loadActionWorkflowVersion: (workflowVersionId: string) => Promise<void>;
  loadVersionList: () => Promise<void>;
  restoreDraftVersion: () => Promise<void>;
  handleDiscardDraft: () => Promise<void>;
  fetchPlatformObjectJsonSchema: (template?: TemplateFilter) => Promise<ObjectSchemaMap>;
  updateActionWorkflowVersion: (actionWorkflowVersion: WorkflowVersion) => void;
  updateInputStep: (inputStep: WorkflowStepInput) => void;
  showMessage: (message: string, messageType: MessageType, persist?: boolean) => unknown;
}

const DashboardAccountActionsShowView = ({
  isLoading,
  isDirty,
  accountTypeId,
  accountAction,
  actionWorkflow,
  actionWorkflowVersion,
  actionWorkflowDeployment,
  validationErrors,
  editingWorkflowVersionId,
  workflowSchema,
  inputStep,
  isLoadingVersionList,
  versionsList,
  loadActionWorkflowVersion,
  fetchPlatformObjectJsonSchema,
  handleArchiveAccountAction,
  handleUpdateAccountActionName,
  handleSaveAccountAction,
  handleActivateAccountAction,
  handleDeactivateAccountAction,
  handlePublishAccountAction,
  loadVersionList,
  restoreDraftVersion,
  handleDiscardDraft,
  updateActionWorkflowVersion,
  updateInputStep,
  showMessage,
}: DashboardAccountActionsShowViewProps) => {
  const hasAccountActionWrite = usePermission(Permission.AccountActionWrite);
  const {
    relationships: {
      'latest-published-version': latestPublishedVersion,
      'latest-draft-version': latestDraftVersion,
    },
  } = actionWorkflow;

  const isVersionArchived =
    actionWorkflowVersion.attributes.status === WorkflowVersionStatus.Archived;
  const isLatestDraft = actionWorkflowVersion.id === latestDraftVersion.data?.id;
  const hasLatestDraft = latestDraftVersion.data?.id != null;
  const isLatestPublishedVersion = actionWorkflowVersion.id === latestPublishedVersion.data?.id;
  const [
    isShowingWorkflowVersionsView,
    { enable: showWorkflowVersionsView, disable: hideWorkflowVersionsView },
  ] = useToggle(false);

  const closeWorkflowVersionView = () => {
    if (actionWorkflowVersion.id !== editingWorkflowVersionId) {
      loadActionWorkflowVersion(editingWorkflowVersionId);
    }
    hideWorkflowVersionsView();
  };

  /**
   * WorkflowVersions are only editable if
   * * They have a latest draft and the current version is the latest draft
   * * They don't have a latest draft and the current version is latest published
   *
   * all other versions are not editable
   */
  const isEditableVersion =
    !isVersionArchived &&
    ((hasLatestDraft && isLatestDraft) || (!hasLatestDraft && isLatestPublishedVersion));
  const editable = isEditableVersion && !isShowingWorkflowVersionsView;
  const isShowingHeaderStatus = isVersionArchived || !isEditableVersion;
  const latestEditableVersionUrl = `dashboard/account-types/${accountTypeId}/actions/${
    accountAction.id
  }/${hasLatestDraft ? latestDraftVersion.data?.id : latestPublishedVersion.data?.id}`;

  const [focusedValidationErrorIndex, setFocusedValidationErrorIndex] = useState<number | null>(
    null,
  );

  const [visibleValidationErrors, { enable: showValidationErrors, disable: hideValidationErrors }] =
    useToggle(false);

  const handleClickWorkflowValidationError = useCallback(
    (errorIndex: number) => {
      setFocusedValidationErrorIndex(errorIndex);
      showValidationErrors();
    },
    [setFocusedValidationErrorIndex, showValidationErrors],
  );

  const handleValidateAndSaveAccountAction = useCallback(async () => {
    const validationErrorsOnSave = validationErrors.filter(
      ({ type }) => type === WorkflowErrorType.Save,
    );
    if (validationErrorsOnSave.length > 0) {
      showMessage('Please fix errors before saving', MessageType.Notice);
      showValidationErrors();
    } else {
      handleSaveAccountAction();
      hideValidationErrors();
    }
  }, [
    validationErrors,
    showMessage,
    showValidationErrors,
    handleSaveAccountAction,
    hideValidationErrors,
  ]);

  const handleValidateAndPublishAccountAction = useCallback(async () => {
    if (validationErrors.length > 0) {
      showMessage('Please fix errors before publishing', MessageType.Notice);
      showValidationErrors();
    } else {
      handlePublishAccountAction();
    }
  }, [validationErrors, showMessage, showValidationErrors, handlePublishAccountAction]);

  const updateVersionConfig = useCallback(
    (versionAttributes: Partial<WorkflowVersion['attributes']>) => {
      updateActionWorkflowVersion({
        ...actionWorkflowVersion,
        attributes: {
          ...actionWorkflowVersion.attributes,
          ...versionAttributes,
        },
      });
    },
    [actionWorkflowVersion, updateActionWorkflowVersion],
  );

  return (
    <>
      <Helmet
        title={`${actionWorkflow.attributes.name} - Actions - ${accountTypeId} - Types - Accounts`}
      />
      {isShowingHeaderStatus && (
        <WorkflowStatusBanner
          isVersionArchived={isVersionArchived}
          isInRollout={false}
          isShowingWorkflowVersions={isShowingWorkflowVersionsView}
          latestEditableVersionUrl={latestEditableVersionUrl}
          showWorkflowVersions={showWorkflowVersionsView}
          versionStatus={actionWorkflowVersion.attributes.status}
          workflowName={actionWorkflow.attributes.name}
        />
      )}
      <AccountActionHeader
        isLoading={isLoading}
        isDirty={isDirty}
        isLatestPublishedVersion={isLatestPublishedVersion}
        isShowingWorkflowVersions={isShowingWorkflowVersionsView}
        accountTypeId={accountTypeId}
        accountAction={accountAction}
        actionWorkflow={actionWorkflow}
        actionWorkflowVersion={actionWorkflowVersion}
        editable={hasAccountActionWrite && editable}
        validationErrors={validationErrors}
        showWorkflowVersionsView={showWorkflowVersionsView}
        closeWorkflowVersionsView={closeWorkflowVersionView}
        handleUpdateAccountActionName={handleUpdateAccountActionName}
        handleArchiveAccountAction={handleArchiveAccountAction}
        handleActivateAccountAction={handleActivateAccountAction}
        handleDeactivateAccountAction={handleDeactivateAccountAction}
        handleSaveAccountAction={handleValidateAndSaveAccountAction}
        handlePublishAccountAction={handleValidateAndPublishAccountAction}
        handleClickWorkflowValidationError={handleClickWorkflowValidationError}
      />

      <WorkflowEditor
        workflowSchema={workflowSchema}
        parentId={actionWorkflow.id}
        parentName={actionWorkflow.attributes.name}
        latestDraftVersionId={actionWorkflow.relationships['latest-draft-version'].data?.id}
        inputStep={inputStep}
        isDirty={isDirty}
        versionConfig={pick(
          ['description', 'outputs', 'steps-config', 'time-out-minutes'],
          actionWorkflowVersion.attributes,
        )}
        versionId={actionWorkflowVersion.id}
        updateVersionConfig={updateVersionConfig}
        setInputStep={updateInputStep}
        loadWorkflowVersion={loadActionWorkflowVersion}
        editable={hasAccountActionWrite && editable}
        validationErrors={visibleValidationErrors ? validationErrors : []}
        isShowingHeaderStatus={isVersionArchived || !isEditableVersion}
        isShowingWorkflowVersions={isShowingWorkflowVersionsView}
        closeWorkflowVersionView={closeWorkflowVersionView}
        workflowDeployment={actionWorkflowDeployment ?? undefined}
        focusedValidationErrorIndex={focusedValidationErrorIndex ?? undefined}
        actionMetaLocation='workflows'
        clearFocusedValidationErrorIndex={() => setFocusedValidationErrorIndex(null)}
        isLoadingVersionList={isLoadingVersionList}
        versionsList={versionsList}
        loadVersionList={loadVersionList}
        restoreDraftVersion={restoreDraftVersion}
        handleDiscardDraft={handleDiscardDraft}
        fetchPlatformObjectJsonSchema={fetchPlatformObjectJsonSchema}
      />
    </>
  );
};

export default DashboardAccountActionsShowView;
